import { useQuery } from "@tanstack/react-query";
import { tournamentApi } from "@bw/shared/api";
import type { PartnerCandidate } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The partner finder's candidate pool (SLICE 1.4-c) — GET
 * /tournaments/draws/{id}/partner-candidates?q= via
 * tournamentApi.getPartnerCandidates. Returns the server's information-rich
 * cards VERBATIM (PartnerCandidate: name, avatar, rating tier, home facility,
 * relationship, entry_id) — no client sort, no derived state, no fabricated
 * candidates.
 *
 * Crucially there is NO ≥2-char gate: an empty / blank query is VALID and still
 * fires (the server returns the whole linkable pool — the finder opens showing
 * everyone). `enabled` therefore depends ONLY on the dialog being open and a
 * draw id being present, never on the query length. Keyed by the trimmed query
 * so each distinct search gets its OWN cache slot; the search box may debounce
 * before changing the query.
 *
 * @param drawId  the draw to search within.
 * @param query   the (raw) search text — trimmed here.
 * @param open    whether the finder dialog is open (gates the fetch).
 * @param limit   optional server-side cap (forwarded as-is when set).
 */
export function usePartnerCandidates(
  drawId: string,
  query: string,
  open: boolean,
  limit?: number,
) {
  const trimmedQuery = (query ?? "").trim();
  return useQuery<PartnerCandidate[]>({
    queryKey: queryKeys.tournaments.partnerCandidates(drawId, trimmedQuery),
    queryFn: () =>
      tournamentApi.getPartnerCandidates(drawId, { q: trimmedQuery, limit }),
    enabled: open && Boolean(drawId),
  });
}
